import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  formatPillCountdown,
  resolveCountdownRuntime,
  parseCountdownFixedEndDate,
} from "../../../utils/countdownDisplay";
import {
  getBannerBackgroundColor,
  getBannerTextColor,
} from "./post-purchase-banner-styles";
import styles from "./PostPurchaseCountdownBanner.module.css";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatPostPurchaseTimer(timeLeft) {
  const minutes = Number(timeLeft.minutes) || 0;
  const seconds = Number(timeLeft.seconds) || 0;
  if (!timeLeft.days && !timeLeft.hours) {
    return `${pad2(minutes)}:${pad2(seconds)}`;
  }
  return formatPillCountdown(timeLeft, {}, { compactWhenUnitsOff: true });
}

export default function PostPurchaseCountdownBanner({ popupData, section }) {
  const countdownTimer = section?.setting?.countdown_timer;
  const blockLayout = countdownTimer?.block_layout || {};
  const urgency = blockLayout.urgency_text || {};
  const bannerBackground = getBannerBackgroundColor(
    blockLayout.banner_status ||
      blockLayout.background_color ||
      blockLayout.status
  );
  const textColor = getBannerTextColor(
    urgency.appearance || blockLayout.text_appearance
  );

  const urgencyText =
    urgency.text ||
    section?.setting?.display?.urgencyText ||
    "Offer expires in: {{timer}}";

  const runtime = resolveCountdownRuntime(popupData, section);
  const { countdown_type, values } = runtime;
  const { days, hours, minutes, seconds, countdownEndTime } = values;

  const movingDurationMs =
    (Number(days) || 0) * 86400000 +
    (Number(hours) || 0) * 3600000 +
    (Number(minutes) || 0) * 60000 +
    (Number(seconds) || 0) * 1000;

  const [endTime, setEndTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    if (countdown_type === "moving_date") {
      if (movingDurationMs <= 0) {
        setEndTime(null);
        return;
      }
      setEndTime(new Date(Date.now() + movingDurationMs));
      return;
    }
    setEndTime(parseCountdownFixedEndDate(countdownEndTime));
  }, [countdown_type, countdownEndTime, movingDurationMs]);

  const updateTimeLeft = useCallback(() => {
    if (!endTime) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const totalSeconds = Math.floor((endTime.getTime() - Date.now()) / 1000);
    if (totalSeconds <= 0) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const d = Math.floor(totalSeconds / 86400);
    const h = Math.floor((totalSeconds % 86400) / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    setTimeLeft({ days: d, hours: h, minutes: m, seconds: s });
  }, [endTime]);

  useEffect(() => {
    updateTimeLeft();
    const id = setInterval(updateTimeLeft, 1000);
    return () => clearInterval(id);
  }, [updateTimeLeft]);

  const timerLabel = formatPostPurchaseTimer(timeLeft);
  const messageParts = String(urgencyText).split("{{timer}}");

  return (
    <div
      className={styles.banner}
      style={{
        backgroundColor: bannerBackground,
        color: textColor,
      }}
    >
      <span className={styles.message}>
        {messageParts.map((part, index) => (
          <React.Fragment key={`pp-banner-${index}`}>
            {part ? <span>{part}</span> : null}
            {index < messageParts.length - 1 ? (
              <span className={styles.timer} style={{ color: textColor }}>
                {timerLabel}
              </span>
            ) : null}
          </React.Fragment>
        ))}
      </span>
    </div>
  );
}

PostPurchaseCountdownBanner.propTypes = {
  popupData: PropTypes.object,
  section: PropTypes.object,
};
